<template>
    <v-container>

        <v-row align="center" no-gutters>
            <v-col cols="6">
                <v-btn
                    class="kimsa-elevation button-container"
                    text color="accent" block x-large
                    @click="currentTab = 'routines'"
                >
                    {{ $t('common.routines') }}
                </v-btn>
            </v-col>
            <v-col cols="6">
                <v-btn
                    class="kimsa-elevation button-container"
                    text color="accent" block x-large
                    @click="currentTab = 'programs'"
                >
                    {{ $t('common.programs') }}
                </v-btn>
            </v-col>
        </v-row>

        <v-row dense>
            <v-col cols="12">
                <v-row align="center" class="mt-3">
                    <v-col cols="12" md="7" class="title-page">
                        {{ title }}
                    </v-col>
                    <v-col cols="10" md="4">
                        <kimsa-text-field
                            @change="search = $event"
                            :value="search"
                            :placeholder="$t('common.search')"
                            force-value clearable
                            icon="mdi-magnify"
                            :loading="loading"
                        />
                    </v-col>
                    <v-col cols="2" md="1" align="center">
                        <v-btn icon @click="refreshCurrentTab">
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </v-col>
                    <v-col cols="12" class="pt-0">
                        <v-divider/>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="7">
                <div class="white-card kimsa-elevation pa-3 fill-height">
                    <v-row dense>
                        <v-col cols="6" md="4">
                            <folder-card create-mode @click="showAdminFolders"/>
                        </v-col>
                        <v-col cols="6" md="4" v-for="folder in lastFolders" :key="folder.id">
                            <folder-card
                                :folder="folder"
                                :is-active="isActiveFolder(folder)"
                                @click="onSelectFolder(folder)"
                                @click:edit="showAdminFolders(folder)"
                            />
                        </v-col>
                        <v-col cols="6" md="3" v-for="folder in currentFolders" :key="folder.id">
                            <folder-card
                                :folder="folder"
                                :is-active="isActiveFolder(folder)"
                                @click="onSelectFolder(folder)"
                                @click:edit="showAdminFolders(folder)"
                            />
                        </v-col>
                        <v-col cols="12" v-if="pageCount > 1">
                            <kimsa-pagination
                                @page="page = $event - 1"
                                :pageCount="pageCount"
                                :total-visible="isMobile ? 5 : 7"
                            />
                        </v-col>
                    </v-row>
                </div>
            </v-col>
            <v-col cols="12" md="5">
                <div class="white-card kimsa-elevation pa-3">
                    <v-tabs-items v-model="currentTab" class="tabs-items">
                        <v-tab-item value="routines">
                            <routines
                                @loading="loading = $event"
                                :search="search"
                                :folder="folderId"
                                :total-visible-pagination="5"
                                ref="routines"/>
                        </v-tab-item>
                        <v-tab-item value="programs">
                            <programs
                                @loading="loading = $event"
                                :search="search"
                                :folder="folderId"
                                ref="programs"/>
                        </v-tab-item>
                    </v-tabs-items>
                </div>
            </v-col>
        </v-row>

        <admin-folders-dialog
            ref="folders"
            :type="currentTab"
            :coach-id="coachId"
            :folder="currentFolder"
            @hide="currentFolder = null"
        />

    </v-container>
</template>

<script>
import Routines from "@/components/coach/routines/Routines";
import Programs from "@/components/coach/routines/Programs";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import {mapActions, mapState} from "vuex";
import AdminFoldersDialog from "@/components/coach/AdminFoldersDialog";
import FolderCard from "@/components/common/cards/FolderCard";
import {db, Timestamp} from "@/utils/Firebase";
import KimsaPagination from "@/components/common/KimsaPagination";

export default {
    components: {KimsaPagination, FolderCard, AdminFoldersDialog, KimsaTextField, Routines, Programs},
    data() {
        return {
            tabs: ['routines', 'programs'],
            currentTab: 'routines',
            search: '',
            loading: false,
            folderId: null,
            currentFolder: null,
            page: 0
        }
    },
    computed: {
        ...mapState(['folders']),
        title(vm = this) {
            if (vm.currentTab === 'routines') return vm.$t('coach.review_your_routines')
            return vm.$t('programs.review_your_programs')
        },
        currentFoldersType(vm = this) {
            return vm.folders.filter(folder => folder.type === vm.currentTab) || []
        },
        lastFolders(vm = this) {
            return vm.currentFoldersType.slice(0,2)
        },
        currentFolders(vm = this) {
            let currentPage = (vm.page * 4) + 2
            return vm.currentFoldersType.slice(currentPage, currentPage + 4)
        },
        coachId(vm = this) {
            return vm.currentUser?.uid || null
        },
        pageCount(vm =this) {
            return Math.ceil((vm.currentFoldersType.length - 2) / 4)
        }
    },
    async mounted() {
        const vm = this
        if (vm.folders.length === 0) await vm.bindFolders(vm.currentUser.uid)
        if (vm.$route.query?.tabs === 'programs') vm.currentTab = 'programs'
    },
    methods: {
        ...mapActions(['bindFolders']),
        refreshCurrentTab() {
            const vm = this
            if (vm.currentTab === 'programs') return vm.$refs.programs.refresh()
            return vm.$refs.routines.loadRoutines()
        },
        showAdminFolders(folder) {
            const vm = this
            if (folder) vm.currentFolder = folder
            vm.$refs.folders.show()
        },
        onSelectFolder(folder) {
            const vm = this
            if (vm.folderId === folder?.id) return vm.folderId = null
            vm.folderId = folder?.id || null
        },
        isActiveFolder(folder) {
            const vm = this
            return vm.folderId === folder.id
        },
    },
    watch: {
        currentTab() {
            this.folderId = null
        }
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            let programRoutes = ['coach.routines.program.detail', 'coach.routines.program.assign']
            if (programRoutes.includes(from.name)) vm.currentTab = 'programs'
        })
    }
}
</script>

<style scoped>

.title-page {
    font-size: 30px;
    font-weight: bold;
    color: #333333;
}

.tabs-items {
    background-color: transparent;
    padding-bottom: 16px;
}

.button-container {
    border: 1px solid currentColor;
    border-radius: 0;
}

.button-container:hover {
    background: var(--v-accent-base);
    color: #FFFFFF !important;
}

</style>
